import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { JsonPipe } from '@angular/common';
import { TranslocoDirective } from '@jsverse/transloco';
import { catchError, of } from 'rxjs';

import { FeedService } from '../../../shared/services/feed.service';
import { FeedDiagnostics } from '../../../shared/models/feed-diagnostics.model';
import { relativeTime } from '../../../shared/utils/relative-time';

/**
 * Diagnostics card for provider types without a dedicated diagnostics view
 * (everything but SIRI): the feed's latest snapshot, timestamps plus the raw
 * provider-specific payload as JSON. 404 = no snapshot yet, never an error.
 */
@Component({
  selector: 'app-feed-raw-diagnostics',
  standalone: true,
  imports: [TranslocoDirective, JsonPipe],
  template: `
    <ng-container *transloco="let t; read: 'providers'">
      <div class="rounded-card bg-card p-6 shadow-hairline">
        <div class="mb-5 font-display text-[12px] font-semibold uppercase tracking-wide text-ink-2">{{ t('feedPage.cards.diagnostics') }}</div>
        @if (isLoading()) {
          <span class="text-[13px] text-ink-3">—</span>
        } @else if (diagnostics(); as d) {
          <div class="mb-4 flex flex-wrap items-center gap-x-6 gap-y-1 text-[12px] text-ink-3">
            <span>{{ t('diagnostics.generated', { time: relativeTime(d.generatedAt) }) }}</span>
            <span>{{ t('diagnostics.received', { time: relativeTime(d.receivedAt) }) }}</span>
          </div>
          <pre class="overflow-x-auto rounded-control bg-sunken p-3 text-[12px] text-ink-2">{{ d.payload | json }}</pre>
        } @else {
          <div class="flex flex-col gap-1.5">
            <span class="text-[14px] font-semibold text-ink-1">{{ t('diagnostics.emptyTitle') }}</span>
            <span class="text-[13px] leading-[19px] text-ink-3">{{ t('diagnostics.emptyBody') }}</span>
          </div>
        }
      </div>
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedRawDiagnosticsComponent {
  private readonly feedService = inject(FeedService);
  private readonly destroyRef = inject(DestroyRef);

  readonly feedId = input.required<string>();

  readonly diagnostics = signal<FeedDiagnostics | null>(null);
  readonly isLoading = signal(true);
  readonly relativeTime = relativeTime;

  private readonly loadOnFeed = effect(() => {
    const feedId = this.feedId();
    untracked(() => {
      this.isLoading.set(true);
      this.feedService
        .getDiagnostics(feedId)
        .pipe(
          catchError(() => of(null)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((res) => {
          this.diagnostics.set(res?.data ?? null);
          this.isLoading.set(false);
        });
    });
  });
}
